/*
 * Copyright (c) 2024. Tooling Team
 */

import PropTypes from "prop-types";
import React, { useState } from "react";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Button } from "@uhg-abyss/web/ui/Button";
import { FileUpload } from "@uhg-abyss/web/ui/FileUpload";
import { Layout } from "@uhg-abyss/web/ui/Layout";
import { FormProvider } from "@uhg-abyss/web/ui/FormProvider";
import { useForm } from "@uhg-abyss/web/hooks/useForm";
import { LoadingSpinner } from "@uhg-abyss/web/ui/LoadingSpinner";
import { resolveBulkUploadFile } from "./BulkUploadUtils";

export default function BulkUploadForm(props) {
  const { formCallback } = props;

  const [file, setFile] = useState({});
  const [isFileValid, setIsFileValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadResetKey, setUploadResetKey] = useState(0);
  const form = useForm();

  const fileTypesAccepted = {
    "text/csv": [".csv"],
  };

  const handleFiles = (inputFile) => {
    const selectedFile = resolveBulkUploadFile(inputFile?.[0]);
    if (selectedFile) {
      setFile(selectedFile);
      if (selectedFile.size === 0) {
        alert("You must choose a file that is not empty to continue.");
        setIsFileValid(false);
      } else {
        setIsFileValid(true);
      }
    }
  };

  //reset state (usually on error)
  const resetUploadState = () => {
    setFile({});
    setIsFileValid(false);
    setIsLoading(false);
    setUploadResetKey((currentKey) => currentKey + 1);
  };

  const uploadFile = async () => {
    setIsLoading(true);
    try {
      const uploadSucceeded = await formCallback(file);
      if (uploadSucceeded === false) {
        resetUploadState();
      }
    } catch (error) {
      resetUploadState();
      return false;
    }
  };

  return (
    <FormProvider state={form} onSubmit={uploadFile}>
      <ModalDialog
        isOpen={props.isOpen}
        onClose={props.onClose}
        header={"Bulk Upload to " + props.activeDomain.value}
        footer={
          <Layout.Group alignLayout="right">
            <Button
              onClick={uploadFile}
              isDisabled={!isFileValid || isLoading}
              isLoading={isLoading}
              ariaLoadingLabel="Validating File"
            >
              Validate
            </Button>
            <Button variant="outline" color="neutral" onClick={props.onClose}>
              Cancel
            </Button>
          </Layout.Group>
        }
      >
        <Layout.Group alignLayout="center">
          <FileUpload
            key={uploadResetKey}
            maxFiles={1}
            label="Acceptable File Types:"
            subText=".csv"
            fileTypes={fileTypesAccepted}
            onChange={(file) => handleFiles(file)}
            model="fileUpload"
          />
          <LoadingSpinner
            ariaLoadingLabel="Validating..."
            isLoading={isLoading}
          />
        </Layout.Group>
      </ModalDialog>
    </FormProvider>
  );
}

BulkUploadForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  bulkUploadPreviewOnClick: PropTypes.func,
  formData: PropTypes.object,
  formCallback: PropTypes.func,
  activeDomain: PropTypes.object,
};
